import React, { useCallback, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, usePaginationFragment, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { DevDetailsDialogLoaderAsync } from '@atlassian/jira-development-details-loader/src/ui/async.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion.graphql';
import type { ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion.graphql';
import type { ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion.graphql';
import type { verionDetailsIssueListQuery } from '@atlassian/jira-relay/src/__generated__/verionDetailsIssueListQuery.graphql';
import { AddIssuesToVersion } from '@atlassian/jira-software-releases-add-issues-to-version/src/ui/async.tsx';
import {
	CollapsibleSection,
	CollapsibleToggleButton,
} from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import { useVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import { useDeepMemo } from '../common/utils/misc.tsx';
import { ErrorBoundary } from '../common/utils/non-critical-error-boundary.tsx';
import {
	useViewVersionDetailsPageExperienceFail,
	useViewVersionDetailsPageExperienceSuccess,
	ViewVersionDetailsPageExperienceSuccessTracker,
} from '../services/experience-tracker/ViewVersionDetailsPageExperienceSuccessTracker.tsx';
import { EmptyView } from './empty-view/index.tsx';
import { ErrorView } from './error-view/ErrorView.tsx';
import { IssueTotalCount } from './issue-total-count/index.tsx';
import { IssueListSkeleton } from './issues-loading-skeleton/IssueListSkeleton.tsx';
import { IssuesMoreButton } from './issues-more-button/async.tsx';
import { Issues } from './issues/index.tsx';
import { ListOptions } from './list-options/index.tsx';
import messages from './messages.tsx';
import { IssueListScreenAnalytics } from './screen-analytics/index.tsx';
import { useIntersectionObserver } from './utils.tsx';

const DEV_DETAILS_DIALOG_SCOPE_ID = 'VersionDetailsIssueList';
const LOAD_MORE_PAGE_SIZE = 50;

interface IssuesRefetchableProps {
	jiraVersion: ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion$key;
}

const IssuesRefetchable = ({ jiraVersion }: IssuesRefetchableProps) => {
	const { isAnyFilterApplied } = useFilterSortingQueryParams();
	const { filters, sortBy } = useVersionIssuesGraphQLVariables();
	const onExperienceSuccess = useViewVersionDetailsPageExperienceSuccess();

	/* eslint-disable @atlassian/relay/unused-fields */
	const versionFragmentRef = usePaginationFragment<
		verionDetailsIssueListQuery,
		ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion$key
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion on JiraVersion
			@argumentDefinitions(first: { type: "Int", defaultValue: 20 }, after: { type: "String" })
			@refetchable(queryName: "verionDetailsIssueListQuery") {
				...issues_softwareReleasesVersionDetailIssueList_jiraVersion
				...issues_softwareReleasesVersionDetailIssueList_jiraVersionRefetch
				...emptyView_softwareReleasesVersionDetailIssueList
				...screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference
				id
				issuesForList: issues(
					first: $first
					after: $after
					filter: ALL
					sortBy: $sortBy
					filters: $filters
				) @required(action: THROW) @connection(key: "ListVersion_issuesForList") {
					totalCount
					jql
					edges @required(action: THROW) {
						...issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference
						...issuesMoreButton_softwareReleasesVersionDetailIssueList_jiraIssueEdges
						...screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference
					}
				}
			}
		`,
		jiraVersion,
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	const { data: jiraVersionData, refetch, hasNext, loadNext, isLoadingNext } = versionFragmentRef;

	const loadNextPage = useCallback(() => {
		if (hasNext) {
			loadNext(LOAD_MORE_PAGE_SIZE);
		}
	}, [loadNext, hasNext]);
	const issueEdges = jiraVersionData.issuesForList.edges.filter(Boolean);
	const totalIssuesCount = jiraVersionData.issuesForList.totalCount ?? issueEdges.length;
	const nextPageSize = Math.min(totalIssuesCount - issueEdges.length, LOAD_MORE_PAGE_SIZE);
	const setLoadMoreElement = useIntersectionObserver({
		margin: 40,
		onIntersection: loadNextPage,
	});

	const variables = useDeepMemo({
		sortBy,
		filters,
	});

	const [debouncedRefetch] = useDebouncedCallback((refetchVariables) => {
		refetch({
			id: jiraVersionData.id,
			// @ts-expect-error TS2698 Spread types may only be created from object
			...refetchVariables,
		});
	}, 500);

	const isFirstRenderRef = useRef(true);

	useEffect(() => {
		if (isFirstRenderRef.current) {
			// Initial rendering is already after the query is executed by react-resource-router, so we don't need to run refetch for initial rendering.
			isFirstRenderRef.current = false;
		} else {
			// @ts-expect-error TS2345 - Argument is not assignable to parameter of type 'SuspenselessParameters'.
			debouncedRefetch(variables);
		}
	}, [debouncedRefetch, variables]);

	return (
		<>
			{issueEdges.length === 0 ? (
				<JSErrorBoundary
					id="jira.software.releases.version.detail.issue.list.empty-view"
					packageName="jira-software-releases-version-detail-issue-list"
					teamName="jolt"
					onError={() => {
						// We don't want to fire experience failure even if the table rendering fails when the table is empty. Table’s empty state doesn’t really show useful information anyway.
						onExperienceSuccess();
					}}
				>
					<EmptyView
						jiraVersion={jiraVersionData}
						isAnyFilterApplied={isAnyFilterApplied}
						textFilter={filters.searchStr ?? null}
					/>
				</JSErrorBoundary>
			) : (
				<Issues
					jiraIssueEdgesReference={issueEdges}
					jiraVersion={jiraVersionData}
					jiraVersionRefetch={jiraVersionData}
				/>
			)}
			<LoadingMoreContainer ref={setLoadMoreElement}>
				{isLoadingNext ? <IssueListSkeleton rowCount={nextPageSize} /> : null}
			</LoadingMoreContainer>
			<ErrorBoundary id="jira.software.releases.version.detail.issue.list.screen-analytics">
				<IssueListScreenAnalytics
					jiraVersionReference={jiraVersionData}
					jiraIssueEdgesReference={issueEdges}
				/>
			</ErrorBoundary>
		</>
	);
};

interface PropsIssueListWithHeader {
	jiraVersion: ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion$key;
}

const IssueListWithHeader = ({ jiraVersion }: PropsIssueListWithHeader) => {
	const { formatMessage } = useIntl();
	const data =
		useFragment<ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion on JiraVersion
				@argumentDefinitions(first: { type: "Int", defaultValue: 20 }, after: { type: "String" }) {
					...ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion
					...listOptions_softwareReleasesVersionDetailIssueList
					...issuesMoreButton_softwareReleasesVersionDetailIssueList_jiraVersion
					...issueTotalCount_softwareReleasesVersionDetailIssueList
					...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
					...ui_softwareReleasesAddIssuesToVersion_AddIssuesToVersion
					id
					canAddAndRemoveIssues
					issuesForList: issues(
						first: $first
						after: $after
						filter: ALL
						sortBy: $sortBy
						filters: $filters
					) @required(action: THROW) @connection(key: "ListVersion_issuesForList") {
						totalCount
						edges @required(action: THROW) {
							...issues_softwareReleasesVersionDetailIssueList_jiraIssueEdgesReference
							...issuesMoreButton_softwareReleasesVersionDetailIssueList_jiraIssueEdges
						}
					}
				}
			`,
			jiraVersion,
		);

	return (
		<ContextualAnalyticsData sourceName="versionDetailsTable" sourceType={SCREEN}>
			<ErrorBoundary id="issuesListDevDetailsDialog">
				<DevDetailsDialogLoaderAsync scopeId={DEV_DETAILS_DIALOG_SCOPE_ID} />
			</ErrorBoundary>
			<CollapsibleSection
				jiraVersion={data}
				uiType="ISSUES"
				renderHeader={(isCollapsed, onToggleClicked) => (
					<HeaderCollapsibleWrapper>
						<HeaderLeftWrapper onClick={onToggleClicked}>
							<Heading size="small" as="h2">
								<Box xcss={sectionTitleStyles}>
									{formatMessage(
										fg('jira-issue-terminology-update-m4')
											? messages.issuesIssueTerminologyRefreshM4
											: messages.issues,
									)}
								</Box>
							</Heading>
							<JSErrorBoundary
								id="jira.software.releases.version.detail.issue-total-count"
								packageName="jira-software-releases-version-detail-issue-list"
								teamName="jolt"
							>
								<IssueTotalCount jiraVersion={data} />
							</JSErrorBoundary>
							<CollapsibleToggleButton isCollapsed={isCollapsed} />
						</HeaderLeftWrapper>
						<HeaderRightWrapper>
							{data.canAddAndRemoveIssues === true && (
								<AddIssueWrapper key="addIssuesButton">
									{/* DO NOT remove this AddIssueWrapper container: https://atlassian.slack.com/archives/CUU6M5U31/p1684888015086739 */}
									<AddIssuesToVersion
										appearance="subtle"
										isIssueListFooterButton
										jiraVersion={data}
										showIcon
										showLabel={false}
									/>
								</AddIssueWrapper>
							)}
							<IssuesMoreButton
								key="issuesMoreButton"
								jiraVersion={data}
								jiraIssueEdges={data.issuesForList.edges.filter(Boolean)}
							/>
						</HeaderRightWrapper>
					</HeaderCollapsibleWrapper>
				)}
				renderBody={() => (
					<>
						<ListOptions jiraVersion={data} />
						<Placeholder
							name="issues-refetchable"
							fallback={<IssueListSkeleton rowCount={10} marginTop={16} />}
						>
							<IssuesRefetchable jiraVersion={data} />
						</Placeholder>
					</>
				)}
			/>
			<ViewVersionDetailsPageExperienceSuccessTracker />
		</ContextualAnalyticsData>
	);
};

interface Props {
	jiraVersion: ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion$key;
}

export const IssueListWithHeaderWithErrorBoundary = ({ jiraVersion }: Props): React.JSX.Element => {
	const jiraVersionData =
		useFragment<ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_softwareReleasesVersionDetailIssueList_IssueListWithHeaderWithErrorBoundary_jiraVersion on JiraVersion {
					...ui_softwareReleasesVersionDetailIssueList_IssueListWithHeader_jiraVersion
				}
			`,
			jiraVersion,
		);
	const onExperienceFail = useViewVersionDetailsPageExperienceFail();

	return (
		<JSErrorBoundary
			fallback={() => <ErrorView />}
			id="jira.software.releases.version.detail.issue.list"
			packageName="jira-software-releases-version-detail-issue-list"
			teamName="jolt"
			onError={(location: string, error: Error) => {
				// We consider the experience to have failed if the issues don't render.
				onExperienceFail(location, error);
			}}
		>
			<IssueListWithHeader jiraVersion={jiraVersionData} />
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingMoreContainer = styled.div({
	width: '100%',
	minHeight: '16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddIssueWrapper = styled.div({
	height: '32px',
	width: '32px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderCollapsibleWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginTop: token('space.500'),
	marginBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLeftWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRightWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const sectionTitleStyles = xcss({
	marginRight: 'space.100',
});
